import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  ArrowRightIcon,
  CreditCardIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { openChoosePayoutMethodModal } from "@js/apps/withdrawal/components/choose-payout-method-modal";
import { RouterLink } from "@js/components/link";
import { Modal } from "@js/components/modal";

import { openSkipBankingInfoSetupModal } from "../skip-banking-info-setup-modal";

import styles from "./styles.module.scss";

export const FreelancerBillingAnnouncementModal = Modal(
  "freelancer-billing-announcement-modal",
  {
    closeButton: true,
    className: styles.modal,
    padding: false,
    keepOnBackdropClick: true,
    closeButtonProps: { variant: "white-violet", sx: { svg: { m: 0 } } },
  },
);

export const openFreelancerBillingAnnoucementModal = () => {
  FreelancerBillingAnnouncementModal.open({
    children: <FreelancerBillingAnnouncementModalContent />,
  });
};

const SUPPORTED_COUNTRIES = [
  {
    region: "Americas",
    countries: [
      "United States",
      "Canada",
      "Mexico",
      "Brazil",
      "Argentina",
      "Chile",
    ],
  },
  {
    region: "Europe",
    countries: [
      "United Kingdom",
      "Germany",
      "France",
      "Spain",
      "Netherlands",
      "Sweden",
    ],
  },
  {
    region: "Asia · Pacific",
    countries: ["Australia", "Japan", "Singapore", "India", "Hong Kong"],
  },
];

export const FreelancerBillingAnnouncementModalContent = () => {
  const handleMaybeLater = () => {
    FreelancerBillingAnnouncementModal.close();
    openSkipBankingInfoSetupModal();
  };

  const handleAddNow = () => {
    FreelancerBillingAnnouncementModal.close();
    openChoosePayoutMethodModal();
  };

  return (
    <Box>
      <Stack className={styles.header} sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
        <Box
          sx={{
            display: "grid",
            placeItems: "center",
            zIndex: 1,
            backgroundColor: "#fff",
            borderRadius: "1rem",
            width: "56px",
            height: "56px",
            mb: 2.25,
            boxShadow: "0px 4px 12px 0px #14141714",
          }}
        >
          <CreditCardIcon sx={{ color: "var(--dark-violet)", fontSize: 26 }} />
        </Box>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            backgroundColor: "#fff",
            borderRadius: "50px",
            padding: "4px 16px",
            mb: 1.5,
            border: "1px solid #5D19742E",
          }}
        >
          <Typography
            component="span"
            variant="label"
            size="small"
            fontWeight={500}
            className="dot-prefix"
            sx={{
              fontSize: "11px !important",
              letterSpacing: "1.2px !important",
              textTransform: "uppercase",
              color: "var(--dark-violet)",
              "--dot-size": "6px",
            }}
          >
            Important
          </Typography>
        </Box>

        <Typography
          component="h2"
          variant="title"
          size="small"
          fontWeight={500}
          sx={{
            maxWidth: "270px",
            mb: 3,
            fontSize: "28px !important",
            lineHeight: "36px !important",
          }}
        >
          Quick update on how we pay talent.
        </Typography>

        <Typography
          component="p"
          variant="paragraph"
          size="small"
          color="grey-1"
          sx={{ fontSize: "14px !important" }}
        >
          To make sure we can pay you when you're hired, we now require banking
          information from a country we can pay into. Banking in a supported
          country gives you the full jobs feed.
        </Typography>
      </Stack>

      <Stack
        sx={{ p: { xs: 2, sm: 3, md: 4 }, pb: { xs: 2, sm: 2.5, md: 3.5 } }}
      >
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          color="grey-1"
          sx={{ textAlign: "center", mb: 2.25, fontSize: "14px !important" }}
        >
          Do you bank in a country we pay into?
          <br />
          Add your info now, or check the list first.
        </Typography>

        <Divider color="light-grey" sx={{ mb: 2.5 }} />

        <Stack sx={{ gap: 2, mb: 3.5 }}>
          {SUPPORTED_COUNTRIES.map(({ region, countries }) => (
            <Box key={region}>
              <Typography
                component="span"
                variant="label"
                size="small"
                fontWeight={500}
                sx={{
                  display: "block",
                  fontSize: "10px !important",
                  letterSpacing: "1.2px !important",
                  textTransform: "uppercase",
                  color: "var(--dark-grey)",
                  mb: 1,
                }}
              >
                {region}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
                {countries.map((country) => (
                  <Chip
                    key={country}
                    label={country}
                    LabelProps={{
                      sx: {
                        color: "var(--grey-1)",
                        fontSize: "12px !important",
                      },
                    }}
                    sx={{ background: "#F9F6FF !important" }}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            component="span"
            variant="paragraph"
            size="medium"
            fontWeight={500}
            sx={{
              color: "var(--dark-violet)",
              fontSize: "13px !important",
            }}
          >
            Don't see your country?{" "}
            <Typography
              variant="link"
              RouterLink={RouterLink}
              component="a"
              href="https://support.usebraintrust.com/hc/en-us/articles/41239692662807-Where-does-Braintrust-send-payments"
              fontWeight={500}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                color: "var(--dark-violet)",
                textDecoration: "none",
                fontSize: "13px !important",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              See full list{" "}
              <ArrowRightIcon sx={{ fontSize: "inherit", color: "inherit" }} />
            </Typography>
          </Typography>
        </Box>

        <Stack
          direction="row"
          sx={{ justifyContent: "center", gap: 1.5, mt: 2 }}
        >
          <Button
            variant="white-violet"
            shape="squared"
            onClick={handleMaybeLater}
            sx={{ fontSize: "14px !important" }}
          >
            Maybe later
          </Button>
          <Button
            variant="dark-violet"
            shape="squared"
            onClick={handleAddNow}
            endIcon={<ArrowRightIcon />}
            sx={{ fontSize: "14px !important" }}
          >
            Yes, add it now
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
